import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router';
import { useAuthFetch } from '../hooks/useAuthFetch';
import ProgressHistoryChart from './ProgressHistoryChart';
import './ProfilePage.css';

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function daysFromNow(dateStr) {
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function daysAgo(dateStr) {
  const diff = new Date() - new Date(dateStr);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

function accuracyTier(pct) {
  if (pct >= 80) return 'high';
  if (pct >= 60) return 'mid';
  return 'low';
}

// A topic needs this many DISTINCT questions answered before it can be called a weakness.
// Without a floor, one question answered wrong is 0% accuracy and outranks a topic with 40
// answered at 45% — a worse recommendation than no recommendation at all.
//
// "Distinct" is load-bearing, and is why the backend counts first attempts: re-answering
// the same question five times is one question's worth of evidence, not five, and the
// study loop re-serves your failures first, so attempts cluster exactly where they would
// do the most damage to this ranking.
const MIN_ANSWERED_FOR_FOCUS = 5;
const FOCUS_AREA_COUNT = 3;

// The window comes from the server, and the range is walked in UTC, because the history
// rows are bucketed on the Postgres server's UTC date. Building the range from the
// browser's local date instead — which is what this did before — shifts it by a day for
// anyone far enough from UTC, and the seeded first point falls off the left edge.
const MAX_HISTORY_DAYS = 400; // belt-and-braces: never spin on a malformed window

function buildDateRange(startStr, endStr) {
  const toUtc = s => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const end = toUtc(endStr);
  const cursor = new Date(toUtc(startStr));
  const days = [];
  while (cursor.getTime() <= end && days.length < MAX_HISTORY_DAYS) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

// Cumulative confidence: carry the last known running total across days with no activity.
function fillGaps(topicRows, dateRange) {
  const lookup = new Map((topicRows || []).map(r => [r.day, r.running_total]));
  let lastKnown = 0;
  return dateRange.map(day => {
    if (lookup.has(day)) lastKnown = lookup.get(day);
    return { day, value: lastKnown };
  });
}

// Cumulative accuracy from the server's raw daily counts. Deliberately NOT the daily
// percentage: on a day with two answers that can only be 0, 50 or 100, which is noise
// rather than a learning curve. Null until the first answer lands, so the chart shows a
// gap instead of a fabricated 0%.
function fillAccuracy(topicRows, dateRange) {
  const lookup = new Map((topicRows || []).map(r => [r.day, r]));
  let answered = 0;
  let correct = 0;
  return dateRange.map(day => {
    const row = lookup.get(day);
    if (row) {
      answered += row.answered;
      correct += row.correct;
    }
    return { day, value: answered > 0 ? Math.round((correct / answered) * 100) : null };
  });
}

export default function ProfilePage() {
  const { instance, accounts } = useMsal();
  const { authFetch } = useAuthFetch();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);

  const account = accounts[0] || {};
  const displayName = account.name || account.username || 'User';
  const email = account.username || '';

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/profile/stats`)
      .then(res => res.json())
      .then(data => { setProfileData(data); setLoading(false); })
      .catch(err => { console.error('Error fetching profile stats:', err); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/api/profile/confidence-history`)
      .then(res => res.json())
      .then(data => {
        // Backend and frontend deploy independently off the same merge, so tolerate a
        // response from a backend that predates the `window` field: fall back to the
        // 7-day local range this used to build.
        const win = data.window || (() => {
          const local = d => d.toLocaleDateString('en-CA');
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 6);
          return { start: local(start), end: local(end) };
        })();

        const dateRange = buildDateRange(win.start, win.end);
        const filled = (data.topics || []).map(t => {
          const confidence = fillGaps(t.data, dateRange);
          const accuracy = fillAccuracy(t.accuracy, dateRange);
          return {
            topic_id: t.topic_id,
            topic_name: t.topic_name,
            series: dateRange.map((day, i) => ({
              day,
              confidence: confidence[i].value,
              accuracy: accuracy[i].value,
            })),
          };
        });
        setHistoryData(filled);
        setHistoryLoading(false);
      })
      .catch(() => setHistoryLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = profileData?.stats || {};
  const topics = profileData?.topics || [];
  const goals = profileData?.goals || [];

  const totalAnswered = parseInt(stats.total_answered || 0);
  const totalCorrect = parseInt(stats.total_correct || 0);
  const accuracyPct = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const studyDays = parseInt(stats.study_days || 0);
  const currentStreak = parseInt(stats.current_streak || 0);
  const longestStreak = parseInt(stats.longest_streak || 0);

  // Deep-link a topic into the Study page. StudyPage rehydrates these three keys on
  // mount; all three are required, so anything missing an id is a no-op. Lifted out of
  // the goal card's inline handler so Focus Areas reuses it rather than copying it.
  function openTopicInStudy({ topic_id, group_id, domain_id }) {
    if (!topic_id || !group_id || !domain_id) return;
    localStorage.setItem('studySelectedDomain', String(domain_id));
    localStorage.setItem('studySelectedGroup', String(group_id));
    localStorage.setItem('studySelectedTopic', String(topic_id));
    navigate('/study');
  }

  // Weakest topics first, gated by MIN_ANSWERED_FOR_FOCUS. `questions_answered` is a count
  // of distinct questions (first attempts), so the floor and the tie-break both measure
  // evidence rather than repetition. Ties break toward the topic with more questions
  // behind it — same accuracy, more evidence.
  const focusAreas = topics
    .map(t => {
      const answered = parseInt(t.questions_answered);
      const correct = parseInt(t.correct);
      return { ...t, answered, pct: answered > 0 ? Math.round((correct / answered) * 100) : 0 };
    })
    .filter(t => t.answered >= MIN_ANSWERED_FOR_FOCUS)
    .sort((a, b) => a.pct - b.pct || b.answered - a.answered)
    .slice(0, FOCUS_AREA_COUNT);

  function goalPace(goal) {
    if (!goal.start_date || !goal.goal_date || goal.target_confidence <= 0) return null;
    const parseLocal = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
    const start = parseLocal(goal.start_date.split('T')[0]);
    const end = parseLocal(goal.goal_date.split('T')[0]);
    const today = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); })();
    const MS = 1000 * 60 * 60 * 24;
    const totalDays = Math.max(1, Math.round((end - start) / MS));
    const elapsed = Math.max(0, Math.round((today - start) / MS));
    const timePct = Math.min(elapsed / totalDays, 1);
    const confidencePct = Math.min(goal.confidence_total / goal.target_confidence, 1);
    const delta = confidencePct - timePct;
    const status = delta >= 0.05 ? 'Ahead of pace' : delta >= -0.05 ? 'On track' : 'Behind pace';
    const statusClass = delta >= 0.05 ? 'ahead' : delta >= -0.05 ? 'ontrack' : 'behind';
    return { timePct, confidencePct, status, statusClass };
  }

  // Push notifications
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  const [notifSupported] = useState(() =>
    'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window
  );
  const [notifPermission, setNotifPermission] = useState(() =>
    notifSupported ? Notification.permission : 'unsupported'
  );
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifHour, setNotifHour] = useState(8);
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifError, setNotifError] = useState(null);
  const [notifHelpOpen, setNotifHelpOpen] = useState(false);

  useEffect(() => {
    if (!notifSupported) return;
    navigator.serviceWorker.ready.then(reg =>
      reg.pushManager.getSubscription().then(sub => setNotifEnabled(!!sub))
    );
  }, [notifSupported]);

  const getRegistration = () => navigator.serviceWorker.ready;

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
  };

  const handleEnableNotifications = async () => {
    setNotifBusy(true);
    setNotifError(null);
    try {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);
      if (permission !== 'granted') return;
      const reg = await getRegistration();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(process.env.REACT_APP_VAPID_PUBLIC_KEY),
      });
      await authFetch(`${process.env.REACT_APP_API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...sub.toJSON(),
          notifyHour: notifHour,
          utcOffsetMinutes: new Date().getTimezoneOffset(),
        }),
      });
      setNotifEnabled(true);
    } catch (err) {
      console.error('Failed to enable notifications:', err);
      setNotifError(err.message || 'Failed to enable notifications.');
    } finally {
      setNotifBusy(false);
    }
  };

  const handleDisableNotifications = async () => {
    setNotifBusy(true);
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await authFetch(`${process.env.REACT_APP_API_URL}/api/push/subscribe`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setNotifEnabled(false);
    } catch (err) {
      console.error('Failed to disable notifications:', err);
    } finally {
      setNotifBusy(false);
    }
  };

  const handleHourChange = async (newHour) => {
    setNotifHour(newHour);
    if (!notifEnabled) return;
    try {
      const reg = await getRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;
      await authFetch(`${process.env.REACT_APP_API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...sub.toJSON(),
          notifyHour: newHour,
          utcOffsetMinutes: new Date().getTimezoneOffset(),
        }),
      });
    } catch (err) {
      console.error('Failed to update notification time:', err);
    }
  };

  const hour12Options = Array.from({ length: 12 }, (_, i) => {
    const h = i + 1; // 1–12
    const hour24 = h === 12 ? 0 : h; // 12 AM = 0, 1 AM = 1, ..., 11 AM = 11
    return { label: `${h} AM`, value: hour24 };
  }).concat(Array.from({ length: 12 }, (_, i) => {
    const h = i + 1;
    const hour24 = h === 12 ? 12 : h + 12; // 12 PM = 12, 1 PM = 13, ..., 11 PM = 23
    return { label: `${h} PM`, value: hour24 };
  }));

  const [goalSort, setGoalSort] = useState('date'); // 'date' | 'confidence' | 'name'

  const sortedGoals = [...goals].sort((a, b) => {
    if (goalSort === 'date') return new Date(a.goal_date) - new Date(b.goal_date);
    if (goalSort === 'confidence') return (b.target_confidence - b.confidence_total) - (a.target_confidence - a.confidence_total);
    return (a.topic_name || '').localeCompare(b.topic_name || '');
  });

  return (
    <>
    <div className="profile-page">
      <div className="profile-inner">

        {/* Header */}
        <div className="profile-header">
          <div className="profile-avatar">{initials(displayName)}</div>
          <div className="profile-header-info">
            <p className="profile-name">{displayName}</p>
            <p className="profile-email">{email}</p>
          </div>
          <button className="profile-signout-btn" onClick={() => instance.logoutRedirect()}>
            Sign out
          </button>
        </div>

        {/* Notifications */}
        {notifSupported && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p className="profile-section-title">Daily Reminders</p>
            {isIos && !isStandalone ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: 0 }}>
                  Open CertDrill from your home screen to enable notifications.
                </p>
                <button onClick={() => setNotifHelpOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--color-accent)', whiteSpace: 'nowrap', padding: 0 }}>
                  How?
                </button>
              </div>
            ) : notifPermission === 'denied' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: 0 }}>
                  Notifications are blocked.
                </p>
                <button onClick={() => setNotifHelpOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--color-accent)', whiteSpace: 'nowrap', padding: 0 }}>
                  How to enable
                </button>
              </div>
            ) : notifEnabled ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                  Remind me at
                </span>
                <select
                  value={notifHour}
                  onChange={e => handleHourChange(Number(e.target.value))}
                  style={{
                    padding: '0.3rem 0.5rem', borderRadius: '6px', fontSize: '0.82rem',
                    border: '1px solid var(--color-border-strong)',
                    background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer',
                  }}
                >
                  {hour12Options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleDisableNotifications}
                  disabled={notifBusy}
                  style={{
                    padding: '0.3rem 0.8rem', borderRadius: '6px', fontSize: '0.82rem',
                    border: '1px solid var(--color-border-strong)',
                    background: 'var(--color-surface)', color: 'var(--color-text-muted)',
                    cursor: notifBusy ? 'wait' : 'pointer',
                  }}
                >
                  Disable
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={handleEnableNotifications}
                  disabled={notifBusy}
                  style={{
                    padding: '0.35rem 1rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 500,
                    border: '1px solid rgba(249,115,22,0.55)',
                    background: 'var(--color-accent-bg-2)', color: 'var(--color-text)',
                    cursor: notifBusy ? 'wait' : 'pointer',
                  }}
                >
                  {notifBusy ? 'Enabling…' : 'Enable daily reminders'}
                </button>
                {notifError && (
                  <p style={{ fontSize: '0.78rem', color: 'var(--color-error, #ef4444)', marginTop: '0.4rem' }}>
                    {notifError}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Goals */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <p className="profile-section-title" style={{ margin: 0 }}>Exam Goals</p>
            {sortedGoals.length > 1 && (
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {[['date', 'Soonest'], ['confidence', 'Confidence'], ['name', 'Name']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setGoalSort(key)}
                    style={{
                      padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 500, cursor: 'pointer',
                      border: '1px solid var(--color-border-strong)',
                      background: goalSort === key ? 'var(--color-accent-bg-2)' : 'var(--color-surface)',
                      color: goalSort === key ? 'var(--color-text)' : 'var(--color-text-muted)',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {loading ? (
            <p className="profile-loading">Loading…</p>
          ) : sortedGoals.length === 0 ? (
            <p className="profile-empty">No exam goals set. Set one from the Study page.</p>
          ) : (
            <div className="profile-goals-list">
              {sortedGoals.map((goal, idx) => {
                const days = daysFromNow(goal.goal_date);
                const overdue = days < 0;
                const pace = goalPace(goal);
                return (
                  <div key={idx} className="profile-goal-card">
                    <div>
                      <div className={`profile-goal-days ${overdue ? 'overdue' : ''}`}>
                        {overdue ? `+${Math.abs(days)}` : days}
                      </div>
                      <div className="profile-goal-days-label">{overdue ? 'days over' : 'days left'}</div>
                    </div>
                    <div className="profile-goal-info">
                      <p
                        className="profile-goal-name"
                        onClick={() => openTopicInStudy(goal)}
                        style={goal.topic_id ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'var(--color-text-subtle)' } : {}}
                      >
                        {goal.topic_name || 'Unknown topic'}
                      </p>
                      <p className="profile-goal-date">
                        Exam: {new Date(goal.goal_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                      {pace && (
                        <div className="profile-goal-pace">
                          <div className="profile-pace-row">
                            <span className="profile-pace-label">Time</span>
                            <div className="profile-pace-bar-track">
                              <div className="profile-pace-bar-fill profile-pace-bar-time" style={{ width: `${Math.round(pace.timePct * 100)}%` }} />
                            </div>
                            <span className="profile-pace-pct">{Math.round(pace.timePct * 100)}%</span>
                          </div>
                          <div className="profile-pace-row">
                            <span className="profile-pace-label">Pace</span>
                            <div className="profile-pace-bar-track">
                              <div className="profile-pace-bar-fill profile-pace-bar-confidence" style={{ width: `${Math.round(pace.confidencePct * 100)}%` }} />
                            </div>
                            <span className="profile-pace-pct">{Math.round(pace.confidencePct * 100)}%</span>
                          </div>
                          <p className={`profile-pace-status profile-pace-status--${pace.statusClass}`}>{pace.status}</p>
                          <p className="profile-pace-detail">{goal.confidence_total} / {goal.target_confidence} confidence pts</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats */}
        <div>
          <p className="profile-section-title">Overview</p>
          <div className="profile-stats-grid">
            <div className="profile-stat-card">
              <span className="profile-stat-value">{loading ? '…' : totalAnswered}</span>
              <span className="profile-stat-label">Questions answered</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{loading ? '…' : `${accuracyPct}%`}</span>
              {/* Attempt-weighted on purpose: this counts every answer including the
                  study loop's re-serves, so it's *recall* accuracy. The per-topic cards
                  and the accuracy chart below count first attempts only. Naming the two
                  differently is the fix for #49 — the gap between them is itself a signal
                  of how much a user leans on repetition. */}
              <span className="profile-stat-label">Recall accuracy</span>
              <span className="profile-stat-sub">First + review attempts</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{loading ? '…' : topics.length}</span>
              <span className="profile-stat-label">Topics studied</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{loading ? '…' : studyDays}</span>
              <span className="profile-stat-label">Study days</span>
            </div>
            <div className="profile-stat-card">
              <span className="profile-stat-value">{loading ? '…' : currentStreak}</span>
              <span className="profile-stat-label">Day streak</span>
              {!loading && (
                <span className="profile-stat-sub">
                  {currentStreak === 0 ? 'Study today to start one' : `Best ${longestStreak}`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Focus Areas — weakest topics first, straight into the Study page */}
        {!loading && topics.length > 0 && (
          <div>
            <p className="profile-section-title">Focus Areas</p>
            {focusAreas.length === 0 ? (
              <p className="profile-empty">
                Answer at least {MIN_ANSWERED_FOR_FOCUS} questions in a topic and your weakest
                ones will show up here.
              </p>
            ) : (
              <div className="profile-focus-list">
                {focusAreas.map(topic => {
                  const tier = accuracyTier(topic.pct);
                  const canJump = Boolean(topic.topic_id && topic.group_id && topic.domain_id);
                  return (
                    <button
                      key={topic.topic_id}
                      type="button"
                      className="profile-focus-row"
                      onClick={() => openTopicInStudy(topic)}
                      disabled={!canJump}
                      aria-label={`Study ${topic.topic_name}. ${topic.pct} percent accuracy across ${topic.answered} answered questions.`}
                    >
                      <span className="profile-focus-name">{topic.topic_name}</span>
                      <span className="profile-focus-bar-track">
                        <span
                          className={`profile-focus-bar-fill ${tier}`}
                          style={{ width: `${topic.pct}%` }}
                        />
                      </span>
                      <span className={`profile-cert-pct ${tier}`}>{topic.pct}%</span>
                      <span className="profile-focus-count">{topic.answered} answered</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Certifications */}
        <div>
          <p className="profile-section-title">Topics</p>
          {loading ? (
            <p className="profile-loading">Loading…</p>
          ) : topics.length === 0 ? (
            <p className="profile-empty">No topics studied yet. Head to the Study page to get started.</p>
          ) : (
            <div className="profile-cert-grid">
              {topics.map(topic => {
                const answered = parseInt(topic.questions_answered);
                const correct = parseInt(topic.correct);
                const pct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
                const tier = accuracyTier(pct);
                return (
                  <div key={topic.topic_id} className="profile-cert-card">
                    <div className="profile-cert-header">
                      <span className="profile-cert-name">{topic.topic_name}</span>
                      <span className={`profile-cert-pct ${tier}`}>{pct}%</span>
                    </div>
                    <div className="profile-cert-bar-track">
                      <div
                        className={`profile-cert-bar-fill ${tier}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="profile-cert-meta">
                      <span>{answered} answered</span>
                      <span>·</span>
                      <span>{daysAgo(topic.last_studied)}</span>
                    </div>
                    <div className="profile-cert-vendor">{topic.vendor}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Progress history — cumulative confidence (effort) + accuracy (learning) */}
        <div>
          <p className="profile-section-title">Progress over time</p>
          {historyLoading ? (
            <p className="profile-loading">Loading…</p>
          ) : !historyData || historyData.length === 0 ? (
            <p className="profile-empty">No study history yet — answer some questions to see progress here.</p>
          ) : (
            <ProgressHistoryChart topics={historyData} />
          )}
        </div>

      </div>
    </div>

    {notifHelpOpen && (
      <div
        onClick={() => setNotifHelpOpen(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ background: 'var(--color-bg)', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '1.5rem', width: '360px', maxWidth: '92vw' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>How to enable notifications</span>
            <button onClick={() => setNotifHelpOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)' }}>✕</button>
          </div>
          {isIos ? (
            <ol style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', paddingLeft: '1.2rem', lineHeight: 1.7, margin: 0 }}>
              <li>Open <strong style={{ color: 'var(--color-text)' }}>Safari</strong> and go to <strong style={{ color: 'var(--color-text)' }}>certdrill.com</strong></li>
              <li>Tap the <strong style={{ color: 'var(--color-text)' }}>Share</strong> button (box with arrow at the bottom)</li>
              <li>Tap <strong style={{ color: 'var(--color-text)' }}>Add to Home Screen</strong></li>
              <li>Open CertDrill from your home screen icon</li>
              <li>Come back to <strong style={{ color: 'var(--color-text)' }}>Profile</strong> and tap <strong style={{ color: 'var(--color-text)' }}>Enable daily reminders</strong></li>
              {notifPermission === 'denied' && (
                <li style={{ marginTop: '0.5rem' }}>If still blocked: go to <strong style={{ color: 'var(--color-text)' }}>Settings → CertDrill → Notifications</strong> and turn them on</li>
              )}
            </ol>
          ) : (
            <ol style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', paddingLeft: '1.2rem', lineHeight: 1.7, margin: 0 }}>
              <li>Click the <strong style={{ color: 'var(--color-text)' }}>icon to the left of the address bar</strong> (a tune/sliders icon in Chrome, or a padlock in Edge/Firefox)</li>
              <li>Find <strong style={{ color: 'var(--color-text)' }}>Notifications</strong> and set it to <strong style={{ color: 'var(--color-text)' }}>Allow</strong></li>
              <li>Reload the page and click <strong style={{ color: 'var(--color-text)' }}>Enable daily reminders</strong></li>
            </ol>
          )}
        </div>
      </div>
    )}
    </>
  );
}
